// [2026-09-02] ADDED — the SSO sign-in gate for the CUSTOMER app.
//
// THIS REPLACES THE EMAIL/PASSWORD LOGIN for customers. There is no password form here on purpose:
// the customer is already signed in to their InCruiter account, and the proctoring dashboard reuses
// that session (the account cookie is verified server-side by app/core/platform_auth.py). "One
// InCruiter login, every InCruiter product" — building a second password here would be the exact
// thing this change removes.
//
// The old LoginScreen.jsx / App.jsx `Login` are NOT deleted — proctoring-frontend-admin still uses
// them for the STAFF door (@incruiter.com password sign-in). This file is only the customer path.
//
// TWO REAL WAYS IN, mirroring the backend's two accepted credentials:
//   1. Deployed on a *.incruiter.com host  -> the browser sends the account cookie automatically.
//      Nothing to type. This is the true SSO path and the one customers actually use.
//   2. On localhost the .incruiter.com cookie is NOT sent to the dev server, so — FOR TESTING ONLY —
//      paste your token_dev once (DevTools -> Application -> Cookies -> token_dev). Same verified
//      call underneath.

import { useState } from 'react'
import { Notice } from '../../ui.jsx'
import { generateUrl } from '../platform/productLinks.js'

// The InCruiter account app the customer signs in at. When the dashboard is opened without a
// session, this is where "Go to InCruiter sign-in" sends them; after signing in there the shared
// .incruiter.com cookie lets them return here already authenticated.
//
// [2026-09-05] CHANGED — derived from this app's own hostname instead of read from an environment
// variable that is not set.
//
// OLD: const ACCOUNT_URL = import.meta.env?.VITE_ACCOUNT_URL || 'https://account.incruiter.com'
//
// VITE_ACCOUNT_URL IS EMPTY IN THE DEPLOYED DEV BUILD (verified 2026-09-05 against the live bundle;
// the full evidence is in productLinks.js). The `||` fallback therefore won every time, so the one
// button on the dev sign-in screen — the button whose entire job is "go and sign in" — sent people
// to the PRODUCTION account app. Signing in there does not produce a dev session, so they come back
// to the same screen with no indication of what went wrong.
//
// A wrong-environment link is worse than a dead one: it succeeds, against real customer data.
const ACCOUNT_URL = generateUrl('account')

export default function PlatformLogin({ mode = 'signin', message, onToken }) {
  const [paste, setPaste] = useState('')
  const [showPaste, setShowPaste] = useState(false)

  return (
    <div className="login-wrap">
      <div className="login-card" style={{ maxWidth: 420 }}>
        <div className="brand" style={{ padding: '0 0 6px' }}>
          <span className="brand-mark">In</span><span>InCruiter Proctoring</span>
        </div>

        {/* [2026-09-04] ADDED — the 'noaccess' branch: signed in, but this company has not granted
            them proctoring (403 NOT_A_MEMBER).

            It deliberately offers NO sign-in button. They are already signed in; another trip
            through the login returns them here with the same 403, so a button would be a loop
            dressed up as an action. The only thing that changes their situation is an
            administrator at their company adding them, so that is what it says. */}
        {mode === 'noaccess' ? (
          <>
            <h1 style={{ fontSize: 22, margin: '10px 0 6px' }}>No access to this dashboard</h1>
            <Notice kind="warn">
              {message || 'Your InCruiter account does not have access to proctoring. Ask an '
                + 'administrator at your company to invite you from the Team screen.'}
            </Notice>
          </>
        ) : mode === 'off' ? (
          // 503 from /me — the deployment has not wired the account-verify URL yet. Not the user's
          // fault, and a "sign in" button would loop them, so we say what is actually wrong.
          <>
            <h1 style={{ fontSize: 22, margin: '10px 0 6px' }}>Dashboard not enabled yet</h1>
            <Notice kind="warn">
              {message || 'The proctoring dashboard is not switched on for this environment yet. '
                + 'Please try again later or contact your InCruiter account manager.'}
            </Notice>
          </>
        ) : (
          <>
            <h1 style={{ fontSize: 22, margin: '10px 0 4px' }}>Welcome</h1>
            <p className="muted" style={{ margin: '0 0 16px', fontSize: 13.5 }}>
              Sign in with your existing InCruiter account to manage your API keys and credits.
            </p>
            {message && <Notice kind="error">{message}</Notice>}

            <a className="btn" href={ACCOUNT_URL}
               style={{ width: '100%', justifyContent: 'center', textDecoration: 'none',
                        display: 'inline-flex', marginTop: 4 }}>
              Go to InCruiter sign-in
            </a>

            {/* Localhost testing only. Hidden behind a link so the normal screen stays clean. */}
            <div style={{ marginTop: 18, borderTop: '1px solid var(--border)', paddingTop: 14 }}>
              <button type="button" className="linklike muted"
                      style={{ background: 'none', border: 0, cursor: 'pointer', fontSize: 12.5,
                               padding: 0, color: 'var(--ink-muted)' }}
                      onClick={() => setShowPaste((v) => !v)}>
                {showPaste ? 'Hide' : 'Testing on localhost?'}
              </button>
              {showPaste && (
                <div style={{ marginTop: 10 }}>
                  <p className="muted" style={{ fontSize: 12, margin: '0 0 8px' }}>
                    Paste your <code>token_dev</code> cookie value (DevTools → Application → Cookies).
                    Used only in this tab; not needed on a real *.incruiter.com deployment.
                  </p>
                  <div className="row" style={{ display: 'flex', gap: 8 }}>
                    <input value={paste} onChange={(e) => setPaste(e.target.value)}
                           placeholder="token_dev value…" style={{ flex: 1 }} />
                    <button type="button" className="btn" disabled={!paste.trim()}
                            onClick={() => onToken(paste.trim())}>Use token</button>
                  </div>
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  )
}
